import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import type { SignUpCompany } from '@/pinia-store/useSignUpStore'
import {
  BasicCompany,
  CompanyResponse,
  DoctorDetailResponse,
  ListCompanyDoctorsResponse,
  ListCompanyUserResponse,
  ListDoctorsCompanyResponse,
  UpdatedUserResponse
} from '../Types/Responses/CompanyResponses'
import {
  CompanyDoctorInvite,
  CompanyDoctorInviteSearchParams,
  CompanyUser,
  CompanyUserChangePassword,
  UpdateUserPayload
} from '../Types/Requests/CompanyRequests'
import { SelectOption } from '@/types'

export default class CompanyService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/companies'
  }

  async listAdminAccess(): Promise<SelectOption[]> {
    const { data } = await request.get<SelectOption[]>(`${this.getCompletePath()}/list-admin`)
    return data
  }

  async listViewAccess(): Promise<SelectOption[]> {
    const { data } = await request.get<SelectOption[]>(`${this.getCompletePath()}/list-view`)
    return data
  }

  async inviteDoctorForCompany(data: CompanyDoctorInvite) {
    try {
      return await request.post(this.getCompletePath('/invite-doctor'), data)
    } catch (e) {
      console.error(e)
      throw e
    }
  }

  async getDoctorById(doctorId: number): Promise<DoctorDetailResponse> {
    try {
      const { data } = await request.get<DoctorDetailResponse>(
        this.getCompletePath(`/mydoctors/${doctorId}`)
      )
      return data
    } catch (e) {
      throw new Error('Ocorreu um erro ao buscar detalhes do médico')
    }
  }

  async getCompanyDoctors(params: CompanyDoctorInviteSearchParams) {
    try {
      const response = await request.get<ListCompanyDoctorsResponse>(
        this.getCompletePath('/doctor'),
        { ...params }
      )
      return {
        data: response.data.data,
        count: response.data.count
      }
    } catch (e) {
      console.error(e)
      throw e
    }
  }

  async changeUserCompanyPassword(id: number, newPassword: CompanyUserChangePassword) {
    return request.put(this.getCompletePath(`/user/${id}/change-password`), newPassword)
  }

  async createCompanyByPublic(data: SignUpCompany) {
    try {
      const formatedBody = {
        name: data.userName,
        email: data.email,
        phone: data.phone1,
        password: data.password,
        confirmPassword: data.confirmPassword,
        companyName: data.companyName,
        fantasyName: data.fantasyName,
        cnpj: data.cnpj,
        companyEmail: data.corporateEmail,
        companyPhone: data.corporatePhone1,
        companyCep: data.cep,
        companyStreet: data.street,
        companyDistrict: data.district,
        companyNumber: data.number,
        companyComplement: data.complement,
        companyUf: data.state,
        companyCity: data.city
      }
      return request.post(this.getCompletePath(), formatedBody)
    } catch (e) {
      throw new Error('Ocorreu um erro ao cadastrar a empresa, por favor tente novamente.')
    }
  }

  async list(): Promise<CompanyResponse> {
    try {
      const { data } = await request.get<CompanyResponse>(this.getCompletePath() + '/my')
      return data
    } catch (e) {
      return { data: [], count: 0 }
    }
  }

  async listBasic(): Promise<BasicCompany[]> {
    try {
      const { data } = await request.get<BasicCompany[]>(this.getCompletePath() + '/my-basic')
      return data
    } catch (e) {
      return []
    }
  }

  async listDoctorsByCompany(
    companyId: number,
    scheduleId: number
  ): Promise<ListDoctorsCompanyResponse> {
    try {
      const { data } = await request.get<ListDoctorsCompanyResponse>(
        `${this.getCompletePath()}/${companyId}/schedule/${scheduleId}/doctors`
      )
      return data
    } catch (e) {
      return {
        count: 0,
        data: []
      }
    }
  }

  async createCompanyUser(data: CompanyUser) {
    return request.post(this.getCompletePath('/user'), data)
  }

  async listCompanyUsers(search?: string): Promise<ListCompanyUserResponse> {
    const response = await request.get<ListCompanyUserResponse>(this.getCompletePath('/user'), {
      search: search ? search : undefined
    })
    return {
      data: response.data.data,
      count: response.data.count
    }
  }

  async persistLocalCompanyList(companiesId: number[]) {
    if (companiesId) {
      try {
        localStorage.setItem('@ME:companies_id', companiesId.join())
      } catch (e) {
        console.error(e)
      }
    }
  }

  getLocalTokenCompanyList(): string | undefined {
    try {
      const companiesId = localStorage.getItem('@ME:companies_id')
      return companiesId ? companiesId : undefined
    } catch (e) {
      console.error(e)
    }
  }

  async updateUser(userId: number, data: UpdateUserPayload): Promise<UpdatedUserResponse> {
    if (!userId) {
      throw new Error('ID do usuário inválido fornecido para atualização.')
    }
    if (!data || Object.keys(data).length === 0) {
      throw new Error('Nenhum dado fornecido para atualização do usuário.')
    }
    try {
      const response = await request.patch(this.getCompletePath(`/user/${userId}`), data)
      return response.data as UpdatedUserResponse
    } catch (error) {
      console.error(`Erro ao atualizar usuário com ID ${userId}:`, error)
      throw new Error('Não foi possível atualizar o usuário. Verifique os dados e tente novamente.')
    }
  }
}
